import type { ReactNode } from "react";
import { SectionEmpty, SectionError, SectionLoading } from "./SectionStates";

/** The empty state: the standard one-line hint, or a page's own view (e.g. a full EmptyState). */
export type QuerySectionEmpty = { hint: string; icon?: ReactNode; action?: ReactNode } | { view: ReactNode };

/**
 * Renders a section's query: loading rows, an error with retry, the empty state when
 * `isEmpty(data)`, or the content. One failing section never blanks the rest of the page.
 */
export function QuerySection<T>({
  query,
  isEmpty,
  empty,
  loadingRows,
  children,
}: Readonly<{
  query: { data: T | undefined; isPending: boolean; isError: boolean; error?: unknown; refetch: () => unknown };
  isEmpty?: (data: T) => boolean;
  empty?: QuerySectionEmpty;
  loadingRows?: number;
  children: (data: T) => ReactNode;
}>) {
  if (query.isPending) return <SectionLoading rows={loadingRows} />;
  if (query.isError || query.data === undefined)
    return <SectionError onRetry={() => void query.refetch()} error={query.error} />;
  if (empty && isEmpty?.(query.data)) return "view" in empty ? empty.view : <SectionEmpty {...empty} />;
  return <>{children(query.data)}</>;
}
